import { Injectable } from '@angular/core';
import {
  calculateDistanceSurchargeEur,
  calculateStopBaseKosten,
  getStopDistanceKmFromBranch
} from './dispatch-planner-distance-surcharge';

export interface AutoDispatchStop {
  id: string;
  lat: number | null;
  lng: number | null;
  geocodingFailed?: boolean;
  kostenGesamt?: number;
  lieferkostenEK?: number;
  montagekostenEK?: number;
}

export interface AutoDispatchInput {
  importStops: AutoDispatchStop[];
  initialTeams: AutoDispatchStop[][];
  branch: { lat: number; lng: number } | null;
  maxRadiusKm?: number;
}

export interface AutoDispatchResult {
  teams: AutoDispatchStop[][];
  remainingImport: AutoDispatchStop[];
  assignedCount: number;
  outsideRadiusCount: number;
  withoutCoordsCount: number;
}

type LatLng = { lat: number; lng: number };

@Injectable({
  providedIn: 'root'
})
export class DispatchPlannerAutoDispatchService {
  /** Entfernung (km) vs. Umsatz-Balance – höher = stärkere Ausgleichung. */
  private readonly umsatzBalanceKmFactor = 40;

  dispatch(input: AutoDispatchInput): AutoDispatchResult {
    const maxRadiusMeters =
      input.maxRadiusKm != null && input.branch ? input.maxRadiusKm * 1000 : null;
    const isWithinRadius = (stop: AutoDispatchStop) =>
      maxRadiusMeters == null ||
      !input.branch ||
      this.distanceFromBranch(input.branch, stop) <= maxRadiusMeters;

    const importMappable = input.importStops.filter(s => this.hasCoords(s) && isWithinRadius(s));
    const outsideRadius = input.importStops.filter(s => this.hasCoords(s) && !isWithinRadius(s));
    const importUnmappable = input.importStops.filter(s => !this.hasCoords(s));

    const teams = input.initialTeams.map(team => [...team]);
    const teamUmsatz = teams.map(team => this.sumUmsatz(team, input.branch));
    const teamCount = Math.max(teams.length, 1);

    const toAssign = [...importMappable].sort(
      (a, b) => this.distanceFromBranch(input.branch, b) - this.distanceFromBranch(input.branch, a)
    );

    for (const stop of toAssign) {
      const options = Array.from({ length: teamCount }, (_, teamIndex) => {
        const current = teams[teamIndex];
        const ownUmsatz = teamUmsatz[teamIndex];
        const otherUmsatz = teamUmsatz.reduce((sum, value, index) => (
          index === teamIndex ? sum : sum + value
        ), 0);
        const insertion = this.bestInsertion(input.branch, current, stop);
        const stopUmsatz = this.getStopUmsatz(stop, input.branch);
        const newOwn = ownUmsatz + stopUmsatz;
        const imbalance = Math.abs(newOwn - (otherUmsatz / Math.max(teamCount - 1, 1)));
        const totalUmsatz = teamUmsatz.reduce((sum, value) => sum + value, 0) + stopUmsatz;
        const umsatzPenaltyKm =
          totalUmsatz > 0 ? (imbalance / totalUmsatz) * this.umsatzBalanceKmFactor : 0;
        const deltaKm = insertion.deltaMeters / 1000;
        const durationPenaltyKm = deltaKm * 0.15;
        return {
          teamIndex,
          index: insertion.index,
          score: deltaKm + durationPenaltyKm + umsatzPenaltyKm
        };
      });

      options.sort((a, b) => a.score - b.score);
      const best = options[0];
      teams[best.teamIndex] = [
        ...teams[best.teamIndex].slice(0, best.index),
        stop,
        ...teams[best.teamIndex].slice(best.index)
      ];
      teamUmsatz[best.teamIndex] += this.getStopUmsatz(stop, input.branch);
    }

    const finalizedTeams = teams.map(team => this.finalizeTeamOrder(team, input.branch));

    return {
      teams: finalizedTeams,
      remainingImport: [...importUnmappable, ...outsideRadius],
      assignedCount: importMappable.length,
      outsideRadiusCount: outsideRadius.length,
      withoutCoordsCount: importUnmappable.length
    };
  }

  private finalizeTeamOrder(stops: AutoDispatchStop[], branch: LatLng | null): AutoDispatchStop[] {
    const mappable = stops.filter(s => this.hasCoords(s));
    const unmappable = stops.filter(s => !this.hasCoords(s));
    if (mappable.length <= 1) {
      return [...mappable, ...unmappable];
    }
    return [...this.nearestNeighborOrder(mappable, branch), ...unmappable];
  }

  private nearestNeighborOrder(stops: AutoDispatchStop[], branch: LatLng | null): AutoDispatchStop[] {
    const remaining = [...stops];
    const ordered: AutoDispatchStop[] = [];
    let current: LatLng | null = branch;

    while (remaining.length) {
      let bestIdx = 0;
      let bestDist = Infinity;
      remaining.forEach((stop, index) => {
        const dist = current
          ? this.haversineMeters(current.lat, current.lng, Number(stop.lat), Number(stop.lng))
          : 0;
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = index;
        }
      });
      const next = remaining.splice(bestIdx, 1)[0];
      ordered.push(next);
      current = { lat: Number(next.lat), lng: Number(next.lng) };
    }

    return ordered;
  }

  private bestInsertion(
    branch: LatLng | null,
    route: AutoDispatchStop[],
    stop: AutoDispatchStop
  ): { index: number; deltaMeters: number } {
    const base = this.routeLengthMeters(branch, route);
    let bestIndex = route.length;
    let bestDelta = Infinity;

    for (let i = 0; i <= route.length; i++) {
      const trial = [...route.slice(0, i), stop, ...route.slice(i)];
      const delta = this.routeLengthMeters(branch, trial) - base;
      if (delta < bestDelta) {
        bestDelta = delta;
        bestIndex = i;
      }
    }

    return { index: bestIndex, deltaMeters: bestDelta };
  }

  private routeLengthMeters(branch: LatLng | null, stops: AutoDispatchStop[]): number {
    const mappable = stops.filter(s => this.hasCoords(s));
    if (!mappable.length) {
      return 0;
    }

    let total = 0;
    let prev: LatLng = branch ?? { lat: Number(mappable[0].lat), lng: Number(mappable[0].lng) };
    const startFromBranch = branch != null;

    if (startFromBranch) {
      for (const stop of mappable) {
        const point = { lat: Number(stop.lat), lng: Number(stop.lng) };
        total += this.haversineMeters(prev.lat, prev.lng, point.lat, point.lng);
        prev = point;
      }
      total += this.haversineMeters(prev.lat, prev.lng, branch.lat, branch.lng);
      return total;
    }

    for (let i = 1; i < mappable.length; i++) {
      total += this.haversineMeters(
        Number(mappable[i - 1].lat),
        Number(mappable[i - 1].lng),
        Number(mappable[i].lat),
        Number(mappable[i].lng)
      );
    }
    return total;
  }

  private distanceFromBranch(branch: LatLng | null, stop: AutoDispatchStop): number {
    if (!branch || !this.hasCoords(stop)) {
      return 0;
    }
    return this.haversineMeters(branch.lat, branch.lng, Number(stop.lat), Number(stop.lng));
  }

  private hasCoords(stop: AutoDispatchStop): boolean {
    return (
      stop.lat != null &&
      stop.lng != null &&
      !stop.geocodingFailed &&
      Number.isFinite(Number(stop.lat)) &&
      Number.isFinite(Number(stop.lng))
    );
  }

  private getStopUmsatz(stop: AutoDispatchStop, branch: LatLng | null): number {
    const base = calculateStopBaseKosten(stop);
    const distanceKm = getStopDistanceKmFromBranch(stop, branch);
    return base + calculateDistanceSurchargeEur(distanceKm);
  }

  private sumUmsatz(stops: AutoDispatchStop[], branch: LatLng | null): number {
    return stops.reduce((sum, stop) => sum + this.getStopUmsatz(stop, branch), 0);
  }

  private haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const earthRadius = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
}
